import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Stack,
  Chip,
  Divider,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RotateLeft as RotateLeftIcon,
  RotateRight as RotateRightIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
  VideoFile as VideoIcon,
  Archive as ArchiveIcon,
  Verified as VerifiedIcon,
  Security as SecurityIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Storage as StorageIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import axiosInstance from '../api/axiosInstance';
import { toast } from 'react-toastify';

const DocumentViewer = ({ open, document, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [documentUrl, setDocumentUrl] = useState(null);

  // Reset state when document changes
  useEffect(() => {
    if (document && open) {
      setLoading(true);
      setError(null);
      setZoom(1);
      setRotation(0);
      setFullscreen(false);
      
      // Get document URL for preview
      if (document.download_url) {
        setDocumentUrl(document.download_url);
      } else {
        setError('No document URL available');
      }
      setLoading(false);
    }
  }, [document, open]);

  // Handle download
  const handleDownload = async () => {
    try {
      // Use actual download URL if available, otherwise fallback to API call
      if (document.actual_download_url) {
        const link = document.createElement('a');
        link.href = document.actual_download_url;
        link.setAttribute('download', document.title || 'document');
        link.setAttribute('target', '_blank');
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        const response = await axiosInstance.get(`/api/v1/patient/documents/${document.id}/download/`, {
          responseType: 'blob',
        });
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', document.title || 'document');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
      
      toast.success('Document downloaded successfully');
    } catch (error) {
      console.error('Error downloading document:', error);
      let errorMessage = 'Failed to download document';
      
      if (error.response?.status === 404) {
        errorMessage = 'Document not found or no longer available';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to download this document';
      } else if (error.code === 'NETWORK_ERROR') {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Download timeout. Please try again.';
      }
      
      toast.error(errorMessage);
    }
  };

  // Handle zoom
  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.25));

  // Handle rotation
  const handleRotateLeft = () => setRotation(prev => prev - 90);
  const handleRotateRight = () => setRotation(prev => prev + 90);

  // Handle fullscreen
  const handleFullscreen = () => setFullscreen(!fullscreen);

  // Get document icon
  const getDocumentIcon = (mimeType, documentType) => {
    if (mimeType?.includes('pdf')) return <PdfIcon />;
    if (mimeType?.includes('image')) return <ImageIcon />;
    if (mimeType?.includes('video')) return <VideoIcon />;
    if (mimeType?.includes('word') || mimeType?.includes('document')) return <DescriptionIcon />;
    if (mimeType?.includes('zip') || mimeType?.includes('rar')) return <ArchiveIcon />;
    return <DescriptionIcon />;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Render document content based on type
  const renderDocumentContent = () => {
    if (!documentUrl) return null;

    const mimeType = document?.mime_type;
    const isImage = mimeType?.includes('image');
    const isPdf = mimeType?.includes('pdf');
    const isVideo = mimeType?.includes('video');
    const isWord = mimeType?.includes('word') || mimeType?.includes('document');

    if (isImage) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            overflow: 'auto',
          }}
        >
          <img
            src={documentUrl}
            alt={document?.title || 'Document'}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              transform: `scale(${zoom}) rotate(${rotation}deg)`,
              transition: 'transform 0.3s ease',
            }}
            onError={() => setError('Failed to load image')}
          />
        </Box>
      );
    }

    if (isPdf) {
      return (
        <Box
          sx={{
            width: '100%',
            height: '600px',
            overflow: 'hidden',
          }}
        >
          <iframe
            src={`${documentUrl}#toolbar=0&navpanes=0&scrollbar=0`}
            width="100%"
            height="100%"
            style={{
              border: 'none',
              transform: `scale(${zoom})`,
              transformOrigin: 'top left',
            }}
            title={document?.title || 'PDF Document'}
            onError={() => setError('Failed to load PDF')}
          />
        </Box>
      );
    }

    if (isWord) {
      return (
        <Box
          sx={{
            width: '100%',
            height: '600px',
            overflow: 'hidden',
          }}
        >
          <iframe
            src={`${documentUrl}`}
            width="100%"
            height="100%"
            style={{
              border: 'none',
            }}
            title={document?.title || 'Word Document'}
            onError={() => setError('Failed to load document')}
          />
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              If the document doesn't display, please download it to view.
            </Typography>
          </Box>
        </Box>
      );
    }

    if (isVideo) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
          }}
        >
          <video
            controls
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
            }}
            onError={() => setError('Failed to load video')}
          >
            <source src={documentUrl} type={mimeType} />
            <track kind="captions" src="" label="English" />
            Your browser does not support the video tag.
          </video>
        </Box>
      );
    }

    // Fallback for other file types
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          p: 3,
        }}
      >
        <DescriptionIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Preview Not Available
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
          This file type ({mimeType}) cannot be previewed in the browser.
        </Typography>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
        >
          Download Document
        </Button>
      </Box>
    );
  };

  if (!document) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={fullscreen ? false : 'lg'}
      fullWidth={fullscreen}
      fullScreen={fullscreen}
      PaperProps={{
        sx: {
          minHeight: fullscreen ? '100vh' : '80vh',
          maxHeight: fullscreen ? '100vh' : '90vh',
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getDocumentIcon(document.mime_type, document.document_type)}
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {document.title}
            </Typography>
          </Box>
          
          <Stack direction="row" spacing={1}>
            {/* Document controls for images */}
            {document.mime_type?.includes('image') && (
              <>
                <Tooltip title="Zoom Out">
                  <IconButton onClick={handleZoomOut} size="small">
                    <ZoomOutIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Zoom In">
                  <IconButton onClick={handleZoomIn} size="small">
                    <ZoomInIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Rotate Left">
                  <IconButton onClick={handleRotateLeft} size="small">
                    <RotateLeftIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Rotate Right">
                  <IconButton onClick={handleRotateRight} size="small">
                    <RotateRightIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
            
            <Tooltip title={fullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
              <IconButton onClick={handleFullscreen} size="small">
                {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Download">
              <IconButton onClick={handleDownload} size="small">
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>
      </DialogTitle>

      <Divider />

      {/* Document Info */}
      <Box sx={{ p: 2, backgroundColor: 'background.default' }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Chip
            label={document.document_type.replace('_', ' ').toUpperCase()}
            color="primary"
            size="small"
          />
          
          {document.is_verified && (
            <Chip
              icon={<VerifiedIcon />}
              label="VERIFIED"
              color="success"
              size="small"
            />
          )}
          
          <Chip
            icon={<StorageIcon />}
            label={formatFileSize(document.file_size)}
            variant="outlined"
            size="small"
          />
          
          <Chip
            icon={<CalendarIcon />}
            label={format(parseISO(document.created_at), 'MMM dd, yyyy')}
            variant="outlined"
            size="small"
          />
          
          {document.doctor_name && (
            <Chip
              icon={<PersonIcon />}
              label={`Dr. ${document.doctor_name}`}
              variant="outlined"
              size="small"
            />
          )}
          
          <Chip
            icon={<SecurityIcon />}
            label={document.visibility.replace('_', ' ').toUpperCase()}
            variant="outlined"
            size="small"
          />
        </Stack>
        
        {document.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {document.description}
          </Typography>
        )}
      </Box>

      <Divider />

      {/* Document Content */}
      <DialogContent sx={{ p: 0, flex: 1, overflow: 'hidden' }}>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '400px',
            }}
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '400px',
            }}
          >
            <Alert severity="error" sx={{ maxWidth: 400 }}>
              {error}
            </Alert>
          </Box>
        ) : (
          renderDocumentContent()
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Typography variant="body2" color="text.secondary">
          {document.mime_type} • {formatFileSize(document.file_size)}
        </Typography>
        
        <Stack direction="row" spacing={1}>
          <Button onClick={handleDownload} startIcon={<DownloadIcon />}>
            Download
          </Button>
          <Button onClick={onClose}>
            Close
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentViewer; 